import React, { useState, useEffect } from 'react';
import './Contact.css';
import SOPModal from '../components/SOPModal';

const Contact: React.FC = () => {
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [sopModalOpen, setSopModalOpen] = useState(false);
  const [submittedAccessCode, setSubmittedAccessCode] = useState('');

  const [formData, setFormData] = useState({
    companyName: '',
    sku: '',
    dailyVolume: '',
    modules: [] as string[],
    message: '',
    contactName: '',
    phone: '',
    email: '',
    referralSources: [] as string[],
    referralSourceEtc: '',
    salesRepDetail: ''
  });

  const [quoteItems, setQuoteItems] = useState<any[]>([]);

  useEffect(() => {
    const fetchItems = () => {
      fetch('/api/quote-items')
        .then(res => res.json())
        .then(data => {
          setQuoteItems(data);
        })
        .catch(err => console.error('Failed to load quote items', err));
    };

    fetchItems();
    const interval = setInterval(fetchItems, 5000);

    return () => clearInterval(interval);
  }, []);

  const referralOptions = [
    '기존 거래처 연계',
    '보세창고 / 항만 관계자 소개',
    '관세사 / 포워더 추천',
    '해외 파트너(중국·동남아 등) 소개',
    '네이버 / 구글 검색',
    '물류•무역 관련 커뮤니티',
    '전시회•박람회 (코엑스, 킨텍스 등)',
    '영업 담당자 직접 접촉',
    '이전 견적 이력 있음',
    '기타 (직접 입력)'
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleModuleChange = (itemKey: string) => {
    setFormData(prev => ({
      ...prev,
      modules: prev.modules.includes(itemKey)
        ? prev.modules.filter(id => id !== itemKey)
        : [...prev.modules, itemKey]
    }));
  };

  const handleReferralChange = (option: string) => {
    setFormData(prev => ({
      ...prev,
      referralSources: prev.referralSources.includes(option)
        ? prev.referralSources.filter(item => item !== option)
        : [...prev.referralSources, option]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.modules.length === 0) {
      alert('필요한 모듈을 최소 1개 이상 선택해주세요.');
      return;
    }

    if (formData.referralSources.length === 0) {
      alert('당사를 알게 된 경로를 최소 1개 이상 선택해주세요.');
      return;
    }

    const payload = { ...formData };

    if (formData.referralSources.includes('영업 담당자 직접 접촉') && (formData as any).salesRepDetail) {
      const repInfo = `[담당자: ${(formData as any).salesRepDetail}]`;
      payload.referralSourceEtc = payload.referralSourceEtc
        ? `${payload.referralSourceEtc} / ${repInfo}`
        : repInfo;
    }

    try {
      const response = await fetch('/api/quotes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Server response error');

      const data = await response.json();
      setSubmittedAccessCode(data.accessCode);
      setSuccessModalOpen(true);

      setFormData({
        companyName: '', sku: '', dailyVolume: '', modules: [], message: '',
        contactName: '', phone: '', email: '', referralSources: [],
        referralSourceEtc: '', salesRepDetail: ''
      });
    } catch (error) {
      console.error('Error saving quote to DB:', error);
      alert('견적 요청 접수 중 오류가 발생했습니다. 잠시 후 다시 시도해주시거나 전화로 문의 바랍니다.');
    }
  };

  const contactInfo = [
    { label: 'Phone', value: '010-4404-1465', href: 'tel:010-4404-1465' },
    { label: 'Email', value: 'khb@chunilkor.co.kr', href: 'mailto:khb@chunilkor.co.kr' },
    { label: 'KakaoTalk', value: 'banimaru2580', href: null },
    { label: 'Response SLA', value: '영업일 기준 24시간 이내 응답', href: null }
  ];

  return (
    <div className="contact-page">
      {/* =================================================================
          Hero — editorial (image bg)
          ================================================================= */}
      <section
        className="contact-hero"
        style={{ backgroundImage: "url(https://gallery.chunilkor.co.kr/ps/contact-hero.jpg)" }}
      >
        <div className="container">
          <div className="hero-eyebrow">
            <span>01 / Contact</span>
            <span className="hero-eyebrow-divider" />
            <span>Quote · Consultation · Partnership</span>
          </div>
          <h1 className="hero-title">견적 · 상담 요청</h1>
          <p className="hero-lede">
            전문 상담팀이 고객의 물류 요구사항을 분석하여 최적의 솔루션을 제안합니다. 6대 모듈 조합 기반 견적이 영업일 24시간 이내 전달됩니다.
          </p>
          <div className="hero-cta-meta">
            <span className="hero-meta-item">
              <span className="hero-meta-label">Tel</span>
              <a href="tel:010-4404-1465" className="hero-meta-value">010-4404-1465</a>
            </span>
            <span className="hero-meta-divider" />
            <span className="hero-meta-item">
              <span className="hero-meta-label">Email</span>
              <a href="mailto:khb@chunilkor.co.kr" className="hero-meta-value">khb@chunilkor.co.kr</a>
            </span>
          </div>
        </div>
      </section>

      {/* =================================================================
          Section 02 — Quote form
          ================================================================= */}
      <section className="contact-section contact-form-section">
        <div className="container">
          <div className="section-header-split">
            <div className="section-header-left">
              <div className="eyebrow">02 / Quote Form</div>
              <h2 className="section-title">견적 요청 폼</h2>
            </div>
            <div className="section-header-right">
              <p className="section-lede">
                필수 항목을 입력하시면 담당자가 내용을 확인 후 회신드립니다. SKU 수·일 출고량·필요 모듈에 따라 맞춤 견적을 구성합니다.
              </p>
            </div>
          </div>

          <div className="contact-content">
            <div className="form-container">
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="companyName">업체명 *</label>
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="sku">SKU 수 *</label>
                    <input type="number" id="sku" name="sku" value={formData.sku} onChange={handleChange} required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="dailyVolume">일 출고량 *</label>
                    <input type="number" id="dailyVolume" name="dailyVolume" value={formData.dailyVolume} onChange={handleChange} required />
                  </div>
                </div>

                <div className="form-group">
                  <label>필요한 모듈 (복수 선택 가능) *</label>
                  <div className="modules-checkbox-group">
                    {quoteItems.length > 0 ? quoteItems.map((item) => (
                      <label key={item.id} className="module-checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.modules.includes(item.item_key)}
                          onChange={() => handleModuleChange(item.item_key)}
                        />
                        <span>{item.item_key}</span>
                      </label>
                    )) : (
                      <p className="loading-note">항목을 불러오는 중...</p>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="message">자유 메모 *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    placeholder="물류 요구사항이나 문의사항을 자유롭게 작성해주세요."
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contactName">담당자명 *</label>
                  <input type="text" id="contactName" name="contactName" value={formData.contactName} onChange={handleChange} required />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">전화번호 *</label>
                    <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">이메일 *</label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required />
                  </div>
                </div>

                <div className="form-group">
                  <label>당사를 알게 된 경로 (복수 선택 가능) *</label>
                  <div className="modules-checkbox-group referral-checkbox-group">
                    {referralOptions.map((option) => (
                      <label key={option} className="module-checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.referralSources.includes(option)}
                          onChange={() => handleReferralChange(option)}
                        />
                        <span>{option}</span>
                      </label>
                    ))}
                  </div>
                  {formData.referralSources.includes('기타 (직접 입력)') && (
                    <input
                      type="text"
                      name="referralSourceEtc"
                      value={formData.referralSourceEtc}
                      onChange={handleChange}
                      placeholder="기타 경로를 입력해주세요"
                      className="etc-input"
                      required
                    />
                  )}
                  {formData.referralSources.includes('영업 담당자 직접 접촉') && (
                    <input
                      type="text"
                      name="salesRepDetail"
                      value={(formData as any).salesRepDetail || ''}
                      onChange={handleChange}
                      placeholder="담당자 이름과 연락처를 입력해주세요"
                      className="etc-input"
                      required
                    />
                  )}
                </div>

                <button type="submit" className="btn-submit">
                  견적 요청하기
                  <span aria-hidden className="arrow">→</span>
                </button>
              </form>
            </div>

            <aside className="contact-info">
              <div className="info-block">
                <div className="eyebrow">Direct Contact</div>
                <h3 className="info-heading">직접 연락</h3>
                <ul className="info-list">
                  {contactInfo.map((info, idx) => (
                    <li key={idx} className="info-row">
                      <span className="info-row-label">{info.label}</span>
                      {info.href ? (
                        <a href={info.href} className="info-row-value link">{info.value}</a>
                      ) : (
                        <span className="info-row-value">{info.value}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="info-block">
                <div className="eyebrow amber">Consultation Hours</div>
                <h3 className="info-heading">상담 시간</h3>
                <p className="info-text">평일 10:00 – 18:00<br />점심 12:00 – 13:00<br />토·일·공휴일 휴무</p>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* =================================================================
          Section 03 — Positioning (navy CTA)
          ================================================================= */}
      <section className="contact-cta-section">
        <div className="container">
          <div className="cta-grid">
            <div className="cta-left">
              <div className="eyebrow light">03 / Partnership</div>
              <h2 className="cta-title">
                천일국제물류는 밴더사의 <span className="accent-amber">수입·납품 운영을</span><br />
                <span className="accent-white">'함께 설계하는' 파트너</span>로 응답합니다.
              </h2>
              <p className="cta-description">
                6대 모듈 조합형 견적으로 고객의 운영 범위를 정의하고, 물량·스펙·납품채널에 맞춘 운영안을 제안합니다.
              </p>
            </div>
            <div className="cta-right">
              <a href="tel:010-4404-1465" className="btn-editorial-light">
                전화 상담
                <span aria-hidden className="arrow">→</span>
              </a>
              <p className="cta-note">영업일 24시간 이내 회신</p>
            </div>
          </div>
        </div>
      </section>

      {/* Success Modal */}
      {successModalOpen && (
        <div className="success-modal-overlay">
          <div className="success-modal-content">
            <div className="eyebrow amber">Quote Received</div>
            <h2 className="success-modal-title">견적 요청 접수 완료</h2>
            <p className="success-modal-body">
              견적 요청이 성공적으로 접수되었습니다.<br />
              담당자가 내용 확인 후 신속히 연락드리겠습니다.
            </p>

            <div className="success-modal-code">
              <span className="success-modal-code-label">SOP 열람 코드</span>
              <span className="success-modal-code-value">{submittedAccessCode}</span>
            </div>

            <p className="success-modal-note">
              이 코드로 SOP 문서 열람이 가능합니다.
            </p>

            <div className="success-modal-actions">
              <button
                className="btn-editorial-dark"
                onClick={() => {
                  setSuccessModalOpen(false);
                  setSopModalOpen(true);
                }}
              >
                SOP 문서 열기
                <span aria-hidden className="arrow">→</span>
              </button>
              <button
                className="btn-editorial-ghost"
                onClick={() => setSuccessModalOpen(false)}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      <SOPModal
        isOpen={sopModalOpen}
        onClose={() => setSopModalOpen(false)}
        initialAccessCode={submittedAccessCode}
      />
    </div>
  );
};

export default Contact;
